import { paths } from "../systems/userinput/paths";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { findAncestorWithComponent } from "../utils/scene-graph";
import isMobile from "../utils/is-mobile";
import nextTick from "../utils/next-tick";
import { ANIMATIONS_DICTIONARY } from "../utils/custom-utils";

export const ANIMATIONS = {
  IDLE: "Idle",
  WALKING: "Walking",
  WALKING2: "walking",
  WALKING_BACKWARD: "WalkingBackwards",
  WALKING_LEFT: "LeftStrafeWalk",
  WALKING_RIGHT: "RightStrafeWalk",
  RUNNING: "Running",
  RUNNING_BACKWARD: "RunningBackward",
  RUNNING_LEFT: "LeftStrafe",
  RUNNING_RIGHT: "RightStrafe",
  FLYING: "Flying",
  SITLOW: "SitLowSeat",
  SITMEDIUM: "SitMediumSeat"
};

const SIT_MAPPER = {
 "low": ANIMATIONS.SITLOW,
  "medium": ANIMATIONS.SITMEDIUM
}

AFRAME.registerComponent("avatar-animation", {
  animations: null,
  clock: null,
  mixer: null,
  isMe: false,

  schema: {
    sitAnimation: {default: null},
    lockCamera: {default: false},
    front: { default: 0 },
    right: { default: 0 },
    lockedRotMatrix: {default: {}}
  },

  init() {
    this.networkedAvatar = findAncestorWithComponent(this.el, 'networked-avatar');
    document.addEventListener("nipple-move", (e) => {
      if(this.isMe) {
        const { front, right } = e.detail;
        this.networkedAvatar?.setAttribute('networked-avatar', { front, right })
      }
    })

    this.animations = new Map();
    this.avatarObject = this.el.object3D.parent.parent;
    this.clock = new THREE.Clock();
    this.userinput = AFRAME.scenes[0].systems.userinput;
    this.isMe = this.el.closest("#avatar-rig") != null;
    this.ikController = findAncestorWithComponent(this.el, 'ik-controller');
    this.setAnimations(this.el);
    this.avatarRig = this.el.closest("#avatar-rig");
    if(this.avatarRig) {
      this.avatarRig.addEventListener('landed', this.onLanded.bind(this))
    }
  },

  tick() {
    if(!this.mixer) return;
    this.mixer.update(this.clock.getDelta());
    this.avatarObject.rotation.set(0,0,0)
    if(this.isSit()) {
      if(this.ikController.components['ik-controller'].hasMaleAnim ) {
        this.avatarObject.position.set(0,-0.25,-0.09)
      } else {
        this.avatarObject.position.set(0,-0.25,-0.12)
      }
      if(!this.attrValue.lockCamera) {
        this.ikController.components['ik-custom-controller'].lockAvatarRotation = false;
      } else {
        this.ikController.components['ik-custom-controller'].lockAvatarRotation = true;
        this.ikController.components['ik-controller'].cameraYRotation.setFromRotationMatrix(this.attrValue.lockedRotMatrix, "YXZ");
      }

      const remoteSit = ( this.animations.get(ANIMATIONS.SITLOW)?.weight 
                        || this.animations.get(ANIMATIONS.SITMEDIUM)?.weight);

      if(!remoteSit) {
        this.sit();
      }
    } else {
      this.avatarObject.position.set(0,0,0)
      this.ikController.components['ik-custom-controller'].lockAvatarRotation = false
    }

    if (this.isMe && !isMobile()) {
      const position = this.userinput.get(paths.actions.characterAcceleration);
      if (position) {
        const [right, front] = position;
        this.el.setAttribute("avatar-animation", { front, right });
        this.networkedAvatar?.setAttribute('networked-avatar', { front, right })
      }
    }
  },

  update() {
    if(this.isSit()) return this.sit();
    if (this.isIdle()) return this.idle();
    this.walking();
  },
  async onLanded(event) {
    this.copiedRotationMatrix = new THREE.Matrix4();
    this.copiedRotationMatrix.extractRotation(this.ikController.components['ik-controller'].cameraForward);
    this.networkedAvatar?.setAttribute('networked-avatar', {lockCamera: true, lockedRotMatrix:this.copiedRotationMatrix})
  },
  isSit() { 
    return !!this.attrValue.sitAnimation;
  },
  sit() {
    switch(this.attrValue.sitAnimation) {
      case(ANIMATIONS.SITLOW):
        this.sitLow()
        break;
      case(ANIMATIONS.SITMEDIUM):
        this.sitMedium()
        break;
      default: 
        break;
    }
  },
  sitLow() {
    this.resetAll(ANIMATIONS.SITLOW);
    this.setEffectiveWeight(ANIMATIONS.SITLOW, 1);
  },
  sitMedium() {
    this.resetAll(ANIMATIONS.SITMEDIUM);
    this.setEffectiveWeight(ANIMATIONS.SITMEDIUM, 1);
  },
  mapSit(animation) {
    const sitAnimation = SIT_MAPPER[animation] ? SIT_MAPPER[animation] : "";
    this.networkedAvatar?.setAttribute('networked-avatar', {sitAnimation, lockCamera: false})
  },
  idle() {
    this.resetAll(ANIMATIONS.IDLE);
    this.setEffectiveWeight(ANIMATIONS.IDLE, 1);
  },

  resetAll(...ignore) {
    this.animations.forEach((animation) => {
      if (ignore.includes(animation.getClip().name)) return;
      animation.setEffectiveWeight(0);
    });
  },

  isIdle() {
    return this.data.front === 0 && this.data.right === 0;
  },

  walking() {
    [
      [ANIMATIONS.WALKING, this.data.front],
      [ANIMATIONS.WALKING2, this.data.front],
      [ANIMATIONS.WALKING_BACKWARD, -this.data.front],
      [ANIMATIONS.WALKING_RIGHT, -this.data.right],
      [ANIMATIONS.WALKING_LEFT, this.data.right],
    ].forEach(([animationName, value]) => this.setEffectiveWeight(animationName, value));
  },

  async loadGLB(url) {
    const loader = new GLTFLoader();
    return new Promise((resolve, reject) => {
      loader.load(url, (gltf2) => {
        resolve(gltf2);
      }, undefined, reject);
    });
  },
  async waitForObject(scene, objectName, timeout = 600000, interval = 100) {
    const startTime = Date.now();

    const checkForObject = () => {
      const object = scene.object3D.getObjectByName(objectName);
      if (object) {
        return Promise.resolve(object);
      } else if (Date.now() - startTime > timeout) {
        return Promise.reject(new Error('Timeout waiting for object'));
      } else {
        return new Promise(resolve => setTimeout(resolve, interval)).then(checkForObject);
      }
    };

    return checkForObject();
  },

  

  async setAnimations(el) {
    try {
      const ikcontroller = findAncestorWithComponent(el, 'ik-controller');
      if (ikcontroller.parentEl == null) return;
      if(ikcontroller.components['ik-controller'] && !ikcontroller.components['ik-controller'].fullBody) return;
      const scene = document.querySelector('a-scene');
      const nameAnim = ikcontroller.components['ik-controller'].hasMaleAnim 
        ? ANIMATIONS_DICTIONARY['male_anim']
        : ANIMATIONS_DICTIONARY['female_anim'];

      const animation = await this.waitForObject(scene, nameAnim);
      this.mixer = new THREE.AnimationMixer(this.avatarObject);  
      await nextTick();

      animation.animations.forEach((animation, index) => { 
        this.animations.set(animation.name, this.mixer.clipAction(animation));
        this.animations.get(animation.name).play();
        this.setEffectiveWeight(animation.name, 0);
      });
  
      this.setEffectiveWeight(ANIMATIONS.IDLE, 1);
    }
    catch(error) {
      this.avatarObject.visible = true;
    }

  },

  setEffectiveWeight(animationName, weight) {
    this.animations.get(animationName)?.setEffectiveWeight(weight);
  },
});
